import { mapGetters } from 'vuex';
import { checkGeolocationStatus } from '@/utilities/geolocation';
import { Roles } from '@/core/constants/roleConstants';
import axios from '@/plugins/axios';

export default {
  data() {
    return {
      startAlertTimeout: null,
      startFetchingGeolocationTimeout: null,
      lastRecordedGeolocationTime: null,
      coordinates: null,
      timeToWaitStartGeolocation: 0,
    };
  },

  computed: {
    ...mapGetters({
      authenticated: 'auth/authenticated',
      user: 'auth/user',
    }),

    channel() {
      return this.$hasAnyRole([
        Roles.REGIONAL_MANAGER,
        Roles.SUB_DIRECTOR,
        Roles.DIRECTOR,
      ])
        ? null
        : this.user.VTWEG;
    },
  },

  watch: {
    coordinates: {
      deep: true,
      handler(newVal) {
        if (newVal.lat) {
          this.cancelGeolocationAlert();
          this.createEmployeeGeolocationRecord();
        }
      },
    },

    lastRecordedGeolocationTime(newVal) {
      if (newVal) {
        this.calculateNextStartGeolocation();
      }
    },

    timeToWaitStartGeolocation(newVal) {
      if (newVal <= 0 || newVal === null) {
        this.startFetchingGeolocation();
      } else {
        this.startFetchingGeolocationTimeout = setTimeout(() => {
          this.startFetchingGeolocation();
        }, newVal);
      }
    },

    $route(to) {
      if (to.name === 'Logout') {
        this.cancelGeolocationAlert();
      }
    },
  },

  mounted() {
    if (!this.shouldShowGeolocationEmployee()) return;

    /*
      JUST LOGGED IN: LOGIN LOCATION IS ALREADY RECORDED IN AUTH/ATTEMPT,
      SO THE NEXT RECORD IS SCHEDULED FROM NOW
    */
    if (localStorage.getItem('firstLoginDone')) {
      localStorage.removeItem('firstLoginDone');
      this.lastRecordedGeolocationTime = new Date().toISOString();
    } else {
      /*
        PAGE RELOAD: THE NEXT RECORD IS SCHEDULED FROM THE LAST ONE SAVED
        IN DB (LAST_GEOLOCATION FROM /INFO)
      */
      this.lastRecordedGeolocationTime = this.user.last_geolocation.created_at;
      this.calculateNextStartGeolocation();
    }

    // Listen for tab visibility changes (user leaving or returning to the page)
    document.addEventListener('visibilitychange', () => {
      if (this.authenticated && !localStorage.getItem('firstLoginDone')) {
        if (document.hidden) {
          // If the user leaves the page, cancel any pending start timer
          clearTimeout(this.startFetchingGeolocationTimeout);
        } else {
          // If the user comes back, recalculate when to start fetching
          this.calculateNextStartGeolocation();
        }
      }
    });
  },

  methods: {
    async startFetchingGeolocation() {
      if (this.authenticated) {
        this.scheduleGeolocationAlert();
        this.coordinates = await this.geolocationGuard();
      }
    },

    async geolocationGuard() {
      const { allowed, coords, message } = await checkGeolocationStatus();
      try {
        if (allowed) return coords;
        this.handleGeolocationDenied(message);
      } catch (err) {
        this.handleGeolocationDenied(
          err?.message || 'Error al validar geolocalización'
        );
      }
    },

    async createEmployeeGeolocationRecord(type) {
      try {
        const response = await axios.post('/employee/location', {
          employee_code: this.user.employee_number,
          latitude: this.coordinates.lat,
          longitude: this.coordinates.lng,
          channel: this.channel,
          role_id: this.user.roles[0].id,
          type,
        });

        this.lastRecordedGeolocationTime = response.data.data.created_at;
      } catch {
        this.$toast.error('Ocurrió un error al registrar la geolocalización');
      }
    },

    calculateNextStartGeolocation() {
      if (this.lastRecordedGeolocationTime) {
        const createdAt = new Date(this.lastRecordedGeolocationTime);
        const milliseconds = this.getGeolocationWaitMinutes() * 60 * 1000;
        const now = new Date();
        this.timeToWaitStartGeolocation = milliseconds - (now - createdAt);
      }
    },

    scheduleGeolocationAlert() {
      this.startAlertTimeout = setTimeout(() => {
        this.$swal({
          title: '¡ALERTA!',
          text: 'Para continuar debe permitir acceder a su ubicación en el navegdor.',
          icon: 'warning',
          type: 'warning',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
        });
      }, 10000);
    },

    handleGeolocationDenied(message) {
      this.$toast.error(message);
      this.$router.push({ name: 'Logout' });
    },

    cancelGeolocationAlert() {
      clearTimeout(this.startAlertTimeout);
      localStorage.removeItem('firstLoginDone');

      if (this.$swal.isVisible()) {
        this.$swal.close();
      }
    },

    shouldShowGeolocationEmployee() {
      const minutes = this.getGeolocationWaitMinutes();
      return (
        this.$hasAnyRole([
          Roles.SELLER,
          Roles.SUB_MANAGER,
          Roles.MANAGER,
          Roles.REGIONAL_MANAGER,
          Roles.SUB_DIRECTOR,
          Roles.DIRECTOR,
        ]) && minutes > 0
      );
    },

    getGeolocationWaitMinutes() {
      return Number(process.env.VUE_APP_GEOLOCATION_EMPLOYEE_WAIT_MINUTES || 0);
    },
  },
};
